import { Text } from "@lmn/design-system";
import IconCalendarEmpty from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCalendarEmpty.js";
import styled from "styled-components";
import { format, parseISO, isValid } from "date-fns";
import { de } from "date-fns/locale";
import media from "@/styles/mediaQueries";

const Dates = styled.time<{ $hasTransparentBg?: boolean; $onDark?: boolean }>(
  ({ theme, $hasTransparentBg, $onDark }) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "4px",
    padding: $hasTransparentBg ? 0 : "4px 8px",
    backgroundColor: $hasTransparentBg
      ? "transparent"
      : theme.kallisto.color.background.static.backgroundLayer01,
    color: $onDark ? theme.kallisto.color.text.static.textOnDark : undefined,
  })
);

const IconWrapper = styled.span(() => ({
  display: "flex",
  flexShrink: 0,
}));

// Wrapping container for all text — icon stays outside and centers against this block.
const TextContent = styled.span(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  rowGap: "0px",
  columnGap: "14px",
}));

const DesktopDuration = styled.span(() => ({
  display: "none",
  whiteSpace: "nowrap",
  ...media.screenSmMin({
    display: "inline",
  }),
}));

const MobileDuration = styled.span(() => ({
  display: "inline",
  whiteSpace: "nowrap",
  ...media.screenSmMin({
    display: "none",
  }),
}));

const TravelDates: React.FC<{
  from: string;
  to: string;
  duration: number;
  // Overrides nights with actual hotel nights ("Nächte im Hotel").
  // Can differ from duration due to overnight flights.
  stayDuration?: number;
  transparentBg?: boolean;
  onDark?: boolean;
}> = ({ from, to, duration, stayDuration, transparentBg = false, onDark = false }) => {
  const textColor = onDark ? "textOnDark" : undefined;
  // Guard against missing/malformed offer dates: parseISO returns an Invalid
  // Date for these, and date-fns format() then throws "RangeError: Invalid time
  // value", crashing the card (IKARUS-G2). Skip the date range when either is
  // invalid; the duration text still renders.
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  const datesValid = isValid(fromDate) && isValid(toDate);
  return (
    <Dates dateTime={`${from}/${to}`} $hasTransparentBg={transparentBg} $onDark={onDark}>
      <IconWrapper>
        <IconCalendarEmpty size={16} />
      </IconWrapper>
      <TextContent>
        {datesValid && (
          <Text variant="BodySmall" weight="regular" color={textColor} style={{ whiteSpace: "nowrap" }}>
            {format(
              fromDate,
              fromDate.getMonth() !== toDate.getMonth() ? "EEE dd MMM" : "EEE dd",
              { locale: de }
            )}{" "}
            - {format(toDate, "EEE dd MMM", { locale: de })}
          </Text>
        )}

        {stayDuration != null && (
          <MobileDuration>
            <Text variant="BodySmall" weight="regular" color={textColor}>
              {stayDuration} {stayDuration === 1 ? "Nacht" : "Nächte"} im Hotel
            </Text>
          </MobileDuration>
        )}

        <DesktopDuration>
          <Text variant="BodySmall" weight="regular" color={textColor}>
            {duration + 1} {duration + 1 === 1 ? "Tag" : "Tage"}
            {stayDuration != null ? "," : " -"}{" "}
            {stayDuration != null
              ? `${stayDuration} ${stayDuration === 1 ? "Nacht" : "Nächte"} im Hotel`
              : `${duration} ${duration === 1 ? "Nacht" : "Nächte"}`}
          </Text>
        </DesktopDuration>
      </TextContent>
    </Dates>
  );
};

export default TravelDates;
